import React, { useEffect } from 'react';
import LegalLayout from '../components/legal/LegalLayout';

const SECTIONS = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    body: (
      <p>
        By accessing or using SupportCare ("the Platform"), you agree to be bound by these
        Terms and Conditions. If you do not agree, do not use the Platform.
      </p>
    ),
  },
  {
    id: 'description',
    title: 'Description of Service',
    body: (
      <>
        <p>
          SupportCare provides a digital platform that enables users to access support-related
          services, tools, and resources. The Platform may include communication tools,
          scheduling, data management, and AI-assisted features.
        </p>
        <div className="callout">
          <strong>Important:</strong> SupportCare does not provide medical advice, diagnosis,
          or treatment. Any information provided is for informational purposes only.
        </div>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: 'Eligibility',
    body: (
      <p>
        You must be at least 18 years old (or the age of majority in your jurisdiction) to use
        the Platform.
      </p>
    ),
  },
  {
    id: 'accounts',
    title: 'User Accounts',
    body: (
      <>
        <ul>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You agree to provide accurate and complete information</li>
          <li>You are responsible for all activity under your account</li>
        </ul>
        <p>
          SupportCare reserves the right to suspend or terminate accounts that violate these terms.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable Use',
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>Use the platform for unlawful purposes</li>
          <li>Upload harmful, abusive, or misleading content</li>
          <li>Attempt to hack, reverse engineer, or disrupt the platform</li>
          <li>Misuse any AI or automated features</li>
        </ul>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    body: (
      <>
        <p>
          All content, software, branding, and materials on the Platform are owned by
          SupportCare or its licensors and are protected by applicable laws.
        </p>
        <p>You may not copy, distribute, or create derivative works without permission.</p>
      </>
    ),
  },
  {
    id: 'data-content',
    title: 'Data and Content',
    body: (
      <p>
        You retain ownership of any data you submit. By using the Platform, you grant
        SupportCare a license to use, store, and process your data to provide and improve
        services.
      </p>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-Party Services',
    body: (
      <p>
        The Platform may integrate with third-party services. SupportCare is not responsible
        for the practices or content of those services.
      </p>
    ),
  },
  {
    id: 'warranties',
    title: 'Disclaimer of Warranties',
    body: (
      <>
        <p>The Platform is provided "as is" and "as available."</p>
        <p>SupportCare makes no warranties regarding:</p>
        <ul>
          <li>Accuracy or reliability</li>
          <li>Availability or uptime</li>
          <li>Fitness for a particular purpose</li>
        </ul>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    body: (
      <>
        <p>To the fullest extent permitted by law, SupportCare shall not be liable for:</p>
        <ul>
          <li>Indirect or consequential damages</li>
          <li>Loss of data, revenue, or profits</li>
          <li>Service interruptions</li>
        </ul>
      </>
    ),
  },
  {
    id: 'indemnification',
    title: 'Indemnification',
    body: (
      <>
        <p>You agree to indemnify and hold harmless SupportCare from claims arising from:</p>
        <ul>
          <li>Your use of the Platform</li>
          <li>Your violation of these terms</li>
        </ul>
      </>
    ),
  },
  {
    id: 'termination',
    title: 'Termination',
    body: (
      <p>
        SupportCare may suspend or terminate access at any time for violations or misuse.
      </p>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing Law',
    body: (
      <p>
        These Terms shall be governed by the laws of the State of California, without regard
        to conflict of law principles.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to Terms',
    body: (
      <p>
        SupportCare may update these Terms at any time. Continued use constitutes acceptance
        of updated terms.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        For questions, contact: <a href="mailto:help@supportcare.io">help@supportcare.io</a>
      </p>
    ),
  },
];

function TermsConditions() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <LegalLayout
      eyebrowIcon="document"
      eyebrowText="Legal"
      title="Terms and"
      titleEm="Conditions"
      lede="The rules of using SupportCare. Please read carefully — using the Platform means you agree to these terms."
      lastUpdated="May 5, 2026"
      intro={(
        <>
          <strong>The short version:</strong> SupportCare is a tool to help you find and apply
          for paid caregiving programs — it doesn't replace medical, legal, or financial advice.
          Use it lawfully and you're good.
        </>
      )}
      sections={SECTIONS}
    />
  );
}

export default TermsConditions;
